import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, LinkSimple, LinkBreak, X, DiscordLogo } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import api from "@/lib/api";

export default function CustomersTab() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("false"); // "false" = unlinked, "true" = linked, "" = all
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [linkFor, setLinkFor] = useState(null); // customer being linked

  async function load() {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (tab) params.linked = tab;
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/customers", { params });
      setRows(r.data);
    } catch (e) {
      toast.error("Failed to load customers");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  async function unlink(c) {
    if (!window.confirm(`Unlink ${c.email || c.name}? Their purchases will become unattributed.`)) return;
    try {
      const r = await api.post(`/customers/${c.id}/unlink`);
      toast.success(`Unlinked · ${r.data.cleared_purchases} purchases cleared`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  const TABS = [
    { key: "false", label: "UNLINKED" },
    { key: "true", label: "LINKED" },
    { key: "", label: "ALL" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="display-heading font-black text-3xl">Customers</h2>
        <div className="kbd-label text-white/40 mt-1">PAYMENT PROFILES · LINK TO DISCORD MEMBERS UNDER CREATORS</div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1 border-b border-white/10">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`customers-tab-${t.label.toLowerCase()}`}
              className={`px-4 py-2 kbd-label border-b-2 transition-colors ${tab === t.key ? "border-neon-cyan text-neon-cyan" : "border-transparent text-white/50 hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="customers-search" placeholder="email, name, or Tebex username… (Enter)"
            className="w-full bg-black/60 border border-white/10 pl-9 pr-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none" />
        </div>
        <button onClick={load} className="ghost-btn px-4 py-2 kbd-label text-white">SEARCH</button>
      </div>

      <div className="card-tactical overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="kbd-label text-left text-white/50 border-b border-white/10">
              <th className="px-6 py-3">CUSTOMER</th><th>TEBEX USER</th><th>NET SPEND</th>
              <th>LINKED TO</th><th></th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {rows.map(c => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`customer-row-${c.id}`}>
                <td className="px-6 py-3">
                  <div className="font-sans">{c.name || "—"}</div>
                  <div className="text-xs text-white/50">{c.email || c.uuid}</div>
                </td>
                <td className="text-white/70">{c.tebex_username || "—"}</td>
                <td className="text-emerald-400">${c.totals?.net ?? 0} <span className="text-white/40 text-xs">({c.totals?.count || 0}×)</span></td>
                <td>
                  {c.discord_id ? (
                    <div>
                      <div className="text-neon-cyan text-xs inline-flex items-center gap-1"><DiscordLogo size={12} /> {c.discord_id}</div>
                      <div className="text-xs text-white/50">{c.creator_name || c.creator_id}</div>
                    </div>
                  ) : <span className="text-white/30">— unlinked</span>}
                </td>
                <td className="px-3">
                  {c.discord_id ? (
                    <button onClick={() => unlink(c)} data-testid={`customer-unlink-${c.id}`} className="ghost-btn px-3 py-1 text-xs text-white inline-flex items-center gap-1 hover:!border-red-500">
                      <LinkBreak size={12} /> UNLINK
                    </button>
                  ) : (
                    <button onClick={() => setLinkFor(c)} data-testid={`customer-link-${c.id}`} className="neon-btn px-3 py-1 text-xs inline-flex items-center gap-1">
                      <LinkSimple size={12} /> LINK
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && <tr><td colSpan={5} className="px-6 py-8 text-white/40 text-center">No customers. Upload a Tebex CSV in the SALES tab first.</td></tr>}
            {loading && <tr><td colSpan={5} className="px-6 py-6 text-center kbd-label text-neon-cyan">// LOADING</td></tr>}
          </tbody>
        </table>
      </div>

      <LinkDrawer customer={linkFor} onClose={() => setLinkFor(null)} onLinked={() => { setLinkFor(null); load(); }} />
    </div>
  );
}

function LinkDrawer({ customer, onClose, onLinked }) {
  const [memberQ, setMemberQ] = useState("");
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (customer) { setMemberQ(""); setMembers([]); } }, [customer]);

  async function searchMembers() {
    try {
      const r = await api.get("/attributions", { params: memberQ.trim() ? { q: memberQ.trim() } : {} });
      setMembers(r.data.slice(0, 25));
    } catch (e) { toast.error("Failed to search members"); }
  }

  async function link(m) {
    setBusy(true);
    try {
      const r = await api.post(`/customers/${customer.id}/link`, { discord_id: m.discord_id, creator_id: m.creator_id });
      toast.success(`Linked · ${r.data.backfilled_purchases} purchases attributed to ${m.creator_name}`);
      onLinked();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  }

  return (
    <Sheet open={!!customer} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="!w-full sm:!w-[520px] !max-w-none bg-obsidian-surface border-l border-white/10 overflow-y-auto text-white">
        <SheetHeader>
          <SheetTitle className="display-heading text-2xl">Link customer</SheetTitle>
          <SheetDescription className="kbd-label text-white/40">
            {customer?.email || customer?.name} → a Discord member under a creator
          </SheetDescription>
        </SheetHeader>
        {customer && (
          <div className="mt-6 space-y-4">
            <div className="card-tactical p-4 text-sm font-mono">
              <div className="kbd-label text-white/50">CUSTOMER</div>
              <div className="font-sans mt-1">{customer.name || "—"}</div>
              <div className="text-white/60 text-xs">{customer.email}</div>
              <div className="text-emerald-400 text-xs mt-1">Net spend ${customer.totals?.net ?? 0} across {customer.totals?.count || 0} payments</div>
            </div>
            <div>
              <div className="kbd-label mb-1">FIND DISCORD MEMBER</div>
              <div className="flex gap-2">
                <input value={memberQ} onChange={(e) => setMemberQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchMembers()}
                  data-testid="link-member-search" placeholder="username or discord id… (Enter)"
                  className="flex-1 bg-black/60 border border-white/10 px-3 py-2 font-mono text-sm focus:border-neon-cyan outline-none" />
                <button onClick={searchMembers} data-testid="link-member-search-btn" className="ghost-btn px-3 py-2 kbd-label text-white">SEARCH</button>
              </div>
              <div className="text-[10px] text-white/40 mt-1 font-mono">Search blank to list all attributed members.</div>
            </div>
            <div className="space-y-2">
              {members.map(m => (
                <div key={m.discord_id} className="card-tactical p-3 flex items-center justify-between">
                  <div>
                    <div className="font-sans text-sm">@{m.username || "unknown"}</div>
                    <div className="text-xs text-white/50 font-mono">{m.discord_id}</div>
                    <div className="text-xs text-neon-cyan">{m.creator_name || "no creator"}</div>
                  </div>
                  <button onClick={() => link(m)} disabled={busy || !m.creator_id} data-testid={`link-to-${m.discord_id}`}
                    className="neon-btn px-3 py-1 text-xs disabled:opacity-40">LINK</button>
                </div>
              ))}
              {members.length === 0 && <div className="text-white/40 text-sm font-mono">No results yet — search above.</div>}
            </div>
            <button onClick={onClose} className="ghost-btn px-4 py-2 text-white kbd-label inline-flex items-center gap-1"><X size={14} /> CLOSE</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
